/** UI strings for the R03 model (rotation and vibration of a diatomic molecule); merged into the photons dictionary. */
export const sv = {
  rvTitle: "Tvåatomig molekyl: rotationsnivåer, besättning och vibrations–rotationsband",
  rvNote:
    "Stel rotator E_l = B l(l+1) med B = ħ²/(2I) och degenerationsgrad 2l+1, harmonisk vibration (n + ½)ħω. Besättningen är Boltzmannfördelningen i en utspädd gas i termisk jämvikt. Isotopfaktorn ändrar m_r vid oförändrade r₀ och k′: I ∝ m_r, ħω ∝ 1/√m_r. CODATA-konstanter.",
  rvViewRotation: "Rotationsnivåer och besättning",
  rvViewBand: "Vibrations–rotationsband n = 0 → 1",
  rvInertia: "I för referensmolekylen (10⁻⁴⁶ kg m²)",
  rvTemperature: "T (K)",
  rvLevel: "Vald nivå l",
  rvVibration: "ħω för referensmolekylen (eV)",
  rvIsotope: "Isotopfaktor m_r′/m_r",
  rvRotationSvg: "Besättning av rotationsnivåerna",
  rvRotationDesc:
    "Staplar visar N_l/N₀ för l = 0 och uppåt; den valda nivån är markerad. Den streckade linjen anger N_l/N₀ = 1, och kurvan visar besättningen per tillstånd, e^{−E_l/kT}, som alltid är mindre än 1 för l ≥ 1.",
  rvBandSvg: "Linjer i vibrations–rotationsbandet",
  rvBandDesc:
    "Absorptionslinjer från n = 0 till n = 1 längs en energiaxel centrerad på ħω. R-grenen (Δl = +1) ligger till höger, P-grenen (Δl = −1) till vänster, med ett glapp vid ħω där ingen linje finns. Linjehöjden är andelen molekyler i begynnelsenivån l.",
  rvAxisLevel: "l",
  rvAxisBand: "E − ħω′ (meV)",
  rvEqual: "N_l = N₀",
  rvCentre: "ħω′",
  rvB: "B′ = ħ²/(2I′) (eV)",
  rvInertiaUsed: "I′ (kg m²)",
  rvKT: "kT (eV)",
  rvLine: "Linje l−1 → l: 2B′l (eV)",
  rvFrequency: "Frekvens f (Hz)",
  rvWavelengthMm: "Våglängd λ (mm)",
  rvRatio: "N_l/N₀ = (2l+1) e^{−E_l/kT}",
  rvPerState: "Per tillstånd e^{−E_l/kT}",
  rvFraction: "N_l/N",
  rvZ: "Z = Σ (2l+1) e^{−E_l/kT}",
  rvPeakContinuous: "l* = √(kT/2B′) − ½",
  rvPeakInteger: "Mest besatta nivå (heltal)",
  rvShown: "Andel av N i ritade nivåer",
  rvHbarOmega: "ħω′ (eV)",
  rvR: "R(l) = ħω′ + 2B′(l+1) (eV)",
  rvRWavelength: "λ för R(l) (μm)",
  rvP: "P(l) = ħω′ − 2B′l (eV)",
  rvPWavelength: "λ för P(l) (μm)",
  rvGap: "Glapp R(0) − P(1) = 4B′ (eV)",
  rvSpacing: "Linjeavstånd 2B′ (eV)",
  rvVibrationRatio: "Vibration per tillstånd e^{−ħω′/kT}",
  rvBeyond: "Vald nivå utanför ritat band",
  rvNone: "—",
  rvYes: "ja",
  rvNo: "nej",
  rvRotationFormula: String.raw`$E_l=B\,l(l+1)$, $B=\hbar^2/(2I)$, $I=m_r r_0^2$ (42.3–42.6). Ren rotationslinje $l-1\to l$: $\Delta E=2Bl$ ($l\ge1$). Termisk jämvikt: $N_l/N_0=(2l+1)e^{-E_l/kT}$ (39.18 med degenerationsgrad $2l+1$); per tillstånd $e^{-E_l/kT}<1$ för $l\ge1$. Kontinuerligt maximum $l^*=\sqrt{kT/2B}-\tfrac12$.`,
  rvBandFormula: String.raw`$E=(n+\tfrac12)\hbar\omega+B\,l(l+1)$ (42.9) med $\Delta n=+1$, $\Delta l=\pm1$: R-gren $\hbar\omega+2B(l+1)$, $l\ge0$; P-gren $\hbar\omega-2Bl$, $l\ge1$. Ingen linje vid $\hbar\omega$; glappet är $4B$. Isotop vid fasta $r_0$ och $k'$: $B\propto1/m_r$, $\hbar\omega\propto1/\sqrt{m_r}$.`,
};

export const en: Record<keyof typeof sv, string> = {
  rvTitle: "Diatomic molecule: rotational levels, populations and the vibration–rotation band",
  rvNote:
    "Rigid rotor E_l = B l(l+1) with B = ħ²/(2I) and degeneracy 2l+1, harmonic vibration (n + ½)ħω. Populations follow the Boltzmann distribution of a dilute gas in thermal equilibrium. The isotope factor changes m_r at fixed r₀ and k′: I ∝ m_r, ħω ∝ 1/√m_r. CODATA constants.",
  rvViewRotation: "Rotational levels and populations",
  rvViewBand: "Vibration–rotation band n = 0 → 1",
  rvInertia: "I of the reference molecule (10⁻⁴⁶ kg m²)",
  rvTemperature: "T (K)",
  rvLevel: "Selected level l",
  rvVibration: "ħω of the reference molecule (eV)",
  rvIsotope: "Isotope factor m_r′/m_r",
  rvRotationSvg: "Populations of the rotational levels",
  rvRotationDesc:
    "Bars show N_l/N₀ for l = 0 upwards; the selected level is marked. The dashed line marks N_l/N₀ = 1, and the curve shows the population per state, e^{−E_l/kT}, which is always below 1 for l ≥ 1.",
  rvBandSvg: "Lines of the vibration–rotation band",
  rvBandDesc:
    "Absorption lines from n = 0 to n = 1 along an energy axis centred on ħω. The R branch (Δl = +1) lies to the right, the P branch (Δl = −1) to the left, with a gap at ħω where there is no line. Line height is the fraction of molecules in the initial level l.",
  rvAxisLevel: "l",
  rvAxisBand: "E − ħω′ (meV)",
  rvEqual: "N_l = N₀",
  rvCentre: "ħω′",
  rvB: "B′ = ħ²/(2I′) (eV)",
  rvInertiaUsed: "I′ (kg m²)",
  rvKT: "kT (eV)",
  rvLine: "Line l−1 → l: 2B′l (eV)",
  rvFrequency: "Frequency f (Hz)",
  rvWavelengthMm: "Wavelength λ (mm)",
  rvRatio: "N_l/N₀ = (2l+1) e^{−E_l/kT}",
  rvPerState: "Per state e^{−E_l/kT}",
  rvFraction: "N_l/N",
  rvZ: "Z = Σ (2l+1) e^{−E_l/kT}",
  rvPeakContinuous: "l* = √(kT/2B′) − ½",
  rvPeakInteger: "Most populated level (integer)",
  rvShown: "Fraction of N in the drawn levels",
  rvHbarOmega: "ħω′ (eV)",
  rvR: "R(l) = ħω′ + 2B′(l+1) (eV)",
  rvRWavelength: "λ of R(l) (μm)",
  rvP: "P(l) = ħω′ − 2B′l (eV)",
  rvPWavelength: "λ of P(l) (μm)",
  rvGap: "Gap R(0) − P(1) = 4B′ (eV)",
  rvSpacing: "Line spacing 2B′ (eV)",
  rvVibrationRatio: "Vibration per state e^{−ħω′/kT}",
  rvBeyond: "Selected level outside the drawn band",
  rvNone: "—",
  rvYes: "yes",
  rvNo: "no",
  rvRotationFormula: String.raw`$E_l=B\,l(l+1)$, $B=\hbar^2/(2I)$, $I=m_r r_0^2$ (42.3–42.6). Pure rotational line $l-1\to l$: $\Delta E=2Bl$ ($l\ge1$). Thermal equilibrium: $N_l/N_0=(2l+1)e^{-E_l/kT}$ (39.18 with degeneracy); per state $e^{-E_l/kT}<1$ for $l\ge1$. Continuous maximum $l^*=\sqrt{kT/2B}-\tfrac12$.`,
  rvBandFormula: String.raw`$E=(n+\tfrac12)\hbar\omega+B\,l(l+1)$ (42.9) with $\Delta n=+1$, $\Delta l=\pm1$: R branch $\hbar\omega+2B(l+1)$, $l\ge0$; P branch $\hbar\omega-2Bl$, $l\ge1$. No line at $\hbar\omega$; the gap is $4B$. Isotope at fixed $r_0$ and $k'$: $B\propto1/m_r$, $\hbar\omega\propto1/\sqrt{m_r}$.`,
};
